import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';

export interface VideoPlayerUrl {
  playerUrl: string;
  signedUrl: string;
  expiresAt?: number;
}

export interface VideoAccessToken {
  token: string;
  expiresAt: number;
  videoId: string;
}

@Injectable({
  providedIn: 'root'
})
export class SecureVideoService {
  private baseUrl = '/api';
  private videoTokenCache = new Map<string, VideoAccessToken>();

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  /**
   * Get a secure player URL for a video
   * This includes token authentication for protected content
   */
  getSecureVideoUrl(videoId: string): Observable<VideoPlayerUrl> {
    // Check cache first
    const cached = this.videoTokenCache.get(videoId);
    if (cached && cached.expiresAt > Date.now()) {
      return of({
        playerUrl: cached.token,
        signedUrl: cached.token,
        expiresAt: cached.expiresAt
      });
    }

    // Request new secure URL from backend
    return this.http.get<VideoPlayerUrl>(`${this.baseUrl}/videos/${videoId}/player-url`).pipe(
      tap(result => {
        // Cache the result
        this.videoTokenCache.set(videoId, {
          token: result.signedUrl,
          expiresAt: result.expiresAt || Date.now() + 3600000, // 1 hour default
          videoId: videoId
        });
      }),
      catchError(error => {
        console.error('Failed to get secure video URL:', error);
        return throwError(error);
      })
    );
  }

  /**
   * Check if user has access to view a video
   * This could check course enrollment, permissions, etc.
   */
  checkVideoAccess(videoId: string, moduleId?: string): Observable<boolean> {
    // For now, assume authenticated users have access
    // In production, check against backend permissions
    return of(this.authService.isAuthenticated());
  }

  /**
   * Clear cached tokens for a video
   */
  clearVideoCache(videoId?: string): void {
    if (videoId) {
      this.videoTokenCache.delete(videoId);
    } else {
      this.videoTokenCache.clear();
    }
  }

  /**
   * Get video metadata including security status
   */
  getVideoMetadata(videoId: string): Observable<any> {
    return this.http.get(`${this.baseUrl}/videos/${videoId}/status`).pipe(
      map(response => ({
        ...response,
        requiresAuth: true, // All videos require authentication
        isSecure: true
      })),
      catchError(error => {
        console.error('Failed to get video metadata:', error);
        return throwError(error);
      })
    );
  }

  /**
   * Build secure video URL with authentication
   * This adds necessary headers or tokens to the video request
   */
  buildSecureVideoUrl(baseUrl: string, videoId: string): Observable<string> {
    return this.getSecureVideoUrl(videoId).pipe(
      map(result => result.signedUrl)
    );
  }

  /**
   * Handle video playback errors
   * Check if error is due to authentication/authorization
   */
  handleVideoError(error: any): Observable<never> {
    if (error.status === 401 || error.status === 403) {
      // Token expired or unauthorized
      console.error('Video access denied:', error);
      
      // Clear cache and retry
      this.clearVideoCache();
      
      // Could implement retry logic here
      return throwError({
        code: 'VIDEO_ACCESS_DENIED',
        message: 'You do not have permission to view this video'
      });
    }
    
    return throwError(error);
  }
}